import CustomScrollbar from 'components/Common/CustomScrollbar';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { manifestos2026 } from 'components/Temporary/Manifestos/data';
import ManifestoComparison from 'components/Temporary/Manifestos/ManifestoComparison';
import { useManifestosSearch } from 'components/Temporary/Manifestos/ManifestosSearch';
import RateLimitManifestos from 'components/Temporary/Manifestos/RateLimitManifestos';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const CHAR_LENGTH = 700;

const ManifestosTracker = () => {
  const tabParameter = getUrlParam('tab');
  const [tabOption, setTabOption] = useState(getUrlParam('tab') ?? 'Senedd');
  const location = useLocation();

  const manifestosToUse =
    tabOption === 'All' ? Object.values(manifestos2026).flat() : manifestos2026[tabOption.toLowerCase()] || [];

  const getPartiesFromHash = () => {
    let parties = [];
    let rawHash = location.hash?.replace('#', '') ?? '';
    let hashParties = rawHash.split('~');
    for (let i = 0; i < hashParties.length; i++) {
      let party = manifestosToUse.find((item) => item.hash === hashParties[i]);
      if (party) {
        parties = [...parties, party];
      }
    }
    return parties.length > 0 ? parties : [];
  };

  const searchQuery = getUrlParam('search');
  const [manifestosSelected, setManifestosSelected] = useState(getPartiesFromHash());
  const [value, setValue] = useState(searchQuery ?? '');
  const [comparison, setComparison] = useState(null);
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const { searchManifestos } = useManifestosSearch();

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const buildHash = (parties) => {
    const partiesHash = parties.map((party) => party.hash).join('~');
    return `#${partiesHash}`;
  };

  const searchValueAreaRef = useRef(null);

  useEffect(() => {
    const textarea = searchValueAreaRef.current;
    if (textarea && value) {
      const len = value.length;
      textarea.setSelectionRange(len, len);
      textarea.focus();
    }
  }, [comparison]);

  useEffect(() => {
    const source = new AbortController();
    const manifestosSelected = getPartiesFromHash();
    if (!!searchQuery && manifestosSelected.length > 0) {
      searchFunctionality({ manifestosSelected, searchValue: searchQuery, source });
    }
    return () => {
      source.abort('Manifesto tracker cancelled by the user');
    };
  }, [searchQuery, searchTrigger]);

  useEffect(() => {
    if (!!tabParameter) {
      setTabOption(tabParameter);
    } else {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('tab', tabOption);
      navigate({ search: searchParams.toString(), hash: location.hash }, { replace: true });
    }
  }, [tabParameter]);

  const searchFunctionality = async ({ manifestosSelected, searchValue = value, source }) => {
    if (manifestosSelected.length === 0) {
      createNotification('danger', 'Please select at least one manifesto to compare');
    } else if (searchValue.trim() === '') {
      createNotification('danger', 'Please enter a value to compare');
    } else {
      try {
        setIsLoadingComparison(true);
        setComparison(null);
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('search', searchValue);
        navigate(
          {
            search: searchParams.toString(),
            hash: buildHash(manifestosSelected),
          },
          { replace: true }
        );
        const results = await searchManifestos({
          manifestosSelected,
          value,
          election2026: true,
          source,
        });
        if (results?.message === 'limit') {
          if (results?.suscribed) {
            createNotification('danger', 'You have reached the limit of 15 searches in the past 24 hours');
          } else {
            setIsOpen(true);
          }
          setIsLoadingComparison(false);
          setComparison(null);
        } else if (!!results) {
          setComparison(results);
          setIsLoadingComparison(false);
        }
      } catch (error) {
        setIsLoadingComparison(false);
      }
    }
  };

  const comparisonSearchFunctionality = () => {
    if (manifestosSelected.length === 0) {
      createNotification('danger', 'Please select at least one manifesto to compare');
    } else {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('search', value);
      navigate(
        {
          search: searchParams.toString(),
          hash: buildHash(manifestosSelected),
        },
        { replace: true }
      );
      if (value === searchQuery && value !== '') {
        setSearchTrigger((prev) => prev + 1);
      }
    }
  };

  return (
    <>
      <div className='bg-salmon flex-grow-1 d-flex flex-column overflow-hidden'>
        {!comparison && !isMobile && <ManifestoTitle />}
        <div className='d-flex flex-column flex-grow-1 mx-auto main-content-wrapper d-flex flex-column overflow-hidden'>
          {isMobile ? (
            <>
              <div className='flex-grow-1 d-flex flex-column'>
                <CustomScrollbar className='simple-scrollbar'>
                  {!comparison && <ManifestoTitle />}
                  <div className='px-3'>
                    <div className='box-rounded box-rounded-medium '>
                      {!!comparison && <BackButton setComparison={setComparison} />}
                      {!comparison && !isLoadingComparison && (
                        <>
                          <ManifestosList
                            manifestosToUse={manifestosToUse}
                            tabOptionState={{ tabOption, setTabOption }}
                            manifestosSelectedState={{ manifestosSelected, setManifestosSelected }}
                            comparisonState={{ comparison, setComparison }}
                            buildHash={buildHash}
                            isLoadingComparison={isLoadingComparison}
                          />
                        </>
                      )}
                      <div className='pb-5'>
                        <ComparisonContent
                          isLoadingComparison={isLoadingComparison}
                          comparisonState={{ comparison, setComparison }}
                          searchValueAreaRef={searchValueAreaRef}
                          searchFunctionality={comparisonSearchFunctionality}
                          manifestosSelected={manifestosSelected}
                          valueState={{ value, setValue }}
                          getPartiesFromHash={getPartiesFromHash}
                        />
                      </div>
                    </div>
                  </div>
                </CustomScrollbar>
              </div>
            </>
          ) : (
            <div className={`manifestos-work-grid px-3 px-lg-5 w-100 pb-4 ${!!comparison ? 'mt-4' : ''}`}>
              <div className='d-flex flex-column box-rounded box-rounded-medium overflow-hidden'>
                {comparison ? (
                  <SummaryPanel manifestosSelected={manifestosSelected} value={value} setComparison={setComparison} />
                ) : (
                  <ManifestosList
                    manifestosToUse={manifestosToUse}
                    tabOptionState={{ tabOption, setTabOption }}
                    manifestosSelectedState={{ manifestosSelected, setManifestosSelected }}
                    comparisonState={{ comparison, setComparison }}
                    buildHash={buildHash}
                    isLoadingComparison={isLoadingComparison}
                  />
                )}
              </div>
              <div className='d-flex flex-column box-rounded d-flex flex-column box-rounded-medium overflow-hidden'>
                <ComparisonContent
                  isLoadingComparison={isLoadingComparison}
                  comparisonState={{ comparison, setComparison }}
                  searchValueAreaRef={searchValueAreaRef}
                  searchFunctionality={comparisonSearchFunctionality}
                  manifestosSelected={manifestosSelected}
                  valueState={{ value, setValue }}
                  getPartiesFromHash={getPartiesFromHash}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <RateLimitManifestos openState={{ isOpen, setIsOpen }} />
    </>
  );
};

const BackButton = ({ setComparison }) => {
  return (
    <div className='px-3 px-lg-5 text-left py-3'>
      <button
        className='general-button d-inline-flex align-items-center rounded-pill bg-main-blue text-white px-4 font-weight-bold paragraph-p3'
        style={{ height: '34px' }}
        onClick={() => setComparison(null)}
      >
        <span className='icon-long-arrow-left mr-2' /> Go back
      </button>
    </div>
  );
};

const SummaryPanel = ({ manifestosSelected, value, setComparison }) => {
  return (
    <CustomScrollbar className='simple-scrollbar'>
      <div className='px-4 py-4'>
        <button
          className='general-button specific-y-padding-6 d-inline-flex align-items-center rounded-pill bg-main-blue text-white px-4 font-weight-bold paragraph-p3 mb-4 third-button'
          onClick={() => setComparison(null)}
        >
          <span className='icon-long-arrow-left mr-2 long-arrow-small d-inline-block-centered centered-1' /> Go back
        </button>
        <div className='mb-4'>
          <p
            className='paragraph-p6 font-weight-bold text-uppercase light-50-text mb-2'
            style={{ letterSpacing: '0.12em' }}
          >
            Manifestos compared
          </p>
          {manifestosSelected.map((manifesto) => (
            <div
              key={manifesto.label}
              className='d-flex align-items-center px-3 py-2 border mb-2 manifesto-selected-rounded bg-grey-60'
            >
              {manifesto.color && (
                <span
                  className='flex-shrink-0 mr-2'
                  style={{
                    width: '9px',
                    height: '9px',
                    background: manifesto.color,
                    borderRadius: '3px',
                    display: 'inline-block',
                  }}
                />
              )}
              <span className='paragraph-p3 font-weight-bold'>{manifesto.label}</span>
            </div>
          ))}
        </div>
        <div>
          <p
            className='paragraph-p6 font-weight-bold text-uppercase light-50-text mb-2'
            style={{ letterSpacing: '0.12em' }}
          >
            Your search
          </p>
          <div className='px-3 py-2  paragraph-p3 manifesto-selected-rounded bg-grey-60 border'>{value}</div>
        </div>
      </div>
    </CustomScrollbar>
  );
};

const getPartyGroupKey = (label) => {
  if (label.includes('Conservative')) return '0';
  if (label.includes('Reform UK')) return '1';
  return `2_${label}`;
};

const ManifestosList = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const tabOptions = ['Senedd', 'Holyrood'];
  const { manifestosToUse, tabOptionState, manifestosSelectedState, comparisonState, buildHash, isLoadingComparison } =
    props ?? {};
  const { manifestosSelected, setManifestosSelected } = manifestosSelectedState ?? {};
  const { comparison, setComparison } = comparisonState ?? {};
  const { tabOption, setTabOption } = tabOptionState ?? {};
  const [shakingItem, setShakingItem] = useState(null);

  const triggerShake = (label) => {
    setShakingItem(label);
    setTimeout(() => setShakingItem(null), 500);
  };

  const displayManifestos =
    tabOption === 'All'
      ? [...manifestosToUse].sort((a, b) => getPartyGroupKey(a.label).localeCompare(getPartyGroupKey(b.label)))
      : manifestosToUse;

  return (
    <div className='d-flex flex-column flex-grow-1 overflow-hidden' style={{ minHeight: 0 }}>
      <div className='px-4 pt-4 pb-2'>
        <h2 className='title-h4-bold mb-1'>Choose manifestos to compare</h2>
        <span className='paragraph-p3 light-50-text d-block specific-bottom-padding-16'>Choose up to 3</span>
        <div className='flex-centered  specific-bottom-padding-10 bg-salmon rounded-pill w-max-content px-1 py-1'>
          {tabOptions.map((option) => (
            <button
              key={option}
              className={`paragraph-p3 general-button paragraph-p2 rounded-pill px-3 specific-y-padding-3  ${tabOption === option ? 'bg-main-blue text-white' : 'light-60-text'}`}
              disabled={isLoadingComparison}
              onClick={() => {
                setManifestosSelected([]);
                const searchParams = new URLSearchParams(location.search);
                searchParams.set('tab', option);
                navigate({ hash: '', search: searchParams.toString() }, { replace: true });
                setTabOption(option);
              }}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className='flex-grow-1 overflow-auto px-4 manifesto-tracker-list'>
        {displayManifestos.map((manifesto) => {
          const { label, link, color } = manifesto;
          return (
            <div
              className={`checkbox-item checkbox-item-empty d-flex ${shakingItem === label ? ' manifesto-item-shake' : ''} ${isLoadingComparison ? 'checkbox-item-disabled' : ''}`}
              key={normalize(label)}
            >
              <input
                type={'checkbox'}
                disabled={isLoadingComparison}
                onChange={(e) => {
                  let newItems = [...manifestosSelected];
                  let itemPosition = newItems.findIndex((item) => item.label === label);
                  let newParties = [];
                  if (itemPosition >= 0) {
                    newItems.splice(itemPosition, 1);
                    newParties = [...newItems];
                  } else {
                    newParties = [...manifestosSelected, manifesto];
                  }
                  if (newParties.length <= 3) {
                    setManifestosSelected(newParties);
                    if (!!comparison) {
                      setComparison(null);
                    }
                    navigate(
                      {
                        hash: buildHash(newParties),
                        search: location?.search,
                      },
                      { replace: true }
                    );
                  } else if (newParties.length > 3) {
                    triggerShake(label);
                    createNotification('danger', 'You can compare up to three manifestos at a time');
                  }
                }}
                name={`manifesto-${normalize(label)}`}
                checked={!!manifestosSelected.find((item) => item.label === label)}
                id={`manifesto-${normalize(label)}-id`}
              />
              <label
                className='mb-0 flex-centered d-flex flex-grow-1 manifesto-tracker-label'
                htmlFor={`manifesto-${normalize(label)}-id`}
              >
                {color && (
                  <span
                    className='d-inline-block flex-shrink-0 specific-right-margin-10'
                    style={{
                      width: '7px',
                      height: '7px',
                      background: color,
                      borderRadius: '2px',
                      verticalAlign: 'middle',
                      marginTop: '-1px',
                    }}
                  />
                )}
                <span className='line-height-1-2' style={{ marginTop: '-1px' }}>
                  {label}
                  {link && (
                    <a
                      href={link}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='reset-link simple-link-text specific-left-margin-8 manifesto-tracker-external-link rounded-pill paragraph-p5 px-2 specific-top-padding-1 specific-bottom-padding-2 border-general border-light-blue hovered-button'
                    >
                      Read
                      <span className='d-inline-block-centered specific-left-margin-5 icon-arrow-open' />
                    </a>
                  )}
                </span>
              </label>
            </div>
          );
        })}
      </div>
      <div className='d-none d-lg-block specific-top-padding-13 pb-3 px-4' style={{ borderTop: '1px dashed #E6DFDB' }}>
        <button
          disabled={isLoadingComparison}
          className='general-button paragraph-p3 light-50-text font-weight-bold'
          onClick={() => {
            setManifestosSelected([]);
            navigate({ hash: '' }, { replace: true });
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

const ManifestoTitle = () => {
  return (
    <section className='pt-lg-4'>
      <div className='container-lg px-0'>
        <div className='col-lg-13 col-xl-11 px-3 px-sm-0 mx-auto text-center py-4'>
          <h1 className='mb-2 manifesto-tracker-title  px-4 px-lg-0'>Manifesto tracker</h1>
          <p className='mb-0 manifesto-tracker-subtext mx-auto light-50-text'>
            PolicyMogul’s manifesto tracker enables you to compare manifestos from the devolved elections, in relation
            to any topic
          </p>
        </div>
      </div>
    </section>
  );
};

const ComparisonContent = (props) => {
  const {
    isLoadingComparison,
    comparisonState,
    searchValueAreaRef,
    searchFunctionality,
    manifestosSelected,
    valueState,
    getPartiesFromHash,
  } = props;
  const { value, setValue } = valueState ?? {};
  const { comparison, setComparison } = comparisonState ?? {};
  const isMobile = useIsMobile();
  const [heightContainer, containerRef] = useHeightContainer();

  const searchQuery = getUrlParam('search') ? decodeURIComponent(getUrlParam('search')) : '';
  const hashes = useRef(
    getPartiesFromHash()
      ?.map((party) => party?.hash ?? party?.value ?? '')
      .join(',')
  );

  return (
    <div className='flex-grow-1 d-flex flex-column overflow-hidden'>
      {isLoadingComparison ? (
        <div
          className='p-5 p-lg-0 flex-grow-1 d-flex flex-column justify-content-center align-items-center'
          style={isMobile ? { height: `${heightContainer - 54}px` } : null}
          ref={containerRef}
        >
          <div>
            <div className={`text-center pb-5 mx-auto ${isMobile ? 'p-5' : 'waiting-screen'}`}>
              <>
                <p className={`mb-2 ${isMobile ? 'title-h4' : ''}`}>
                  <strong>{`PolicyMogul is analysing manifestos for you`}</strong>
                </p>
                <p>This typically takes 10 - 20 seconds, but may take longer</p>
              </>
            </div>
            <div className='waiting-bar mx-auto'>
              <div className='waiting-bar-indicator' />
            </div>
          </div>
        </div>
      ) : (
        <>
          {comparison ? (
            <ManifestoComparison
              searchValue={value}
              setComparison={setComparison}
              manifestosSelected={manifestosSelected}
              comparison={comparison}
              trackerPage={true}
              hideSummaryHeader={!isMobile}
            />
          ) : (
            <div className='flex-grow-1 d-flex flex-column overflow-hidden'>
              <div className='pt-4 specific-x-padding-30 d-flex align-items-center justify-content-between pb-3'>
                <h2 className='title-h4-bold'>Describe your organisation’s interests</h2>
                <p className={`text-right mb-0 paragraph-p4 font-weight-bold light-50-text`}>
                  <span className={`${value.length > CHAR_LENGTH ? 'text-red-warning' : ''}`}>
                    {thousandCommas(value.length)}
                  </span>{' '}
                  / {thousandCommas(CHAR_LENGTH)}
                </p>
              </div>
              <div className='specific-x-padding-30 d-flex flex-column flex-grow-1 overflow-auto pb-3'>
                <textarea
                  ref={searchValueAreaRef}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      e.preventDefault();
                      if (value.length <= CHAR_LENGTH) {
                        searchFunctionality({ manifestosSelected });
                      }
                    }
                  }}
                  className='general-transition writing-textarea p-3 border rounded manifestos-textarea flex-grow-1 bg-grey-60'
                  placeholder='Tell us about one of your policy interests using natural language '
                  autoFocus={true}
                />
              </div>
              <div className='px-4 pb-4'>
                {(!value || value.trim() === '') && (
                  <div
                    className='d-flex flex-wrap align-items-center specific-top-margin-10 general-transition'
                    style={{ gap: '6px' }}
                  >
                    <span
                      className='paragraph-p4 light-50-text font-weight-bold text-uppercase mr-1'
                      style={{ letterSpacing: '0.12em' }}
                    >
                      Try
                    </span>
                    {[
                      { label: 'Housing & affordability', query: 'Housing supply and affordability' },
                      { label: 'NHS waiting lists', query: 'NHS waiting lists and workforce' },
                      { label: 'Green jobs', query: 'Offshore wind and green jobs' },
                      { label: 'Rural transport', query: 'Rural transport and bus services' },
                    ].map(({ label, query }) => (
                      <button
                        key={label}
                        className='manifesto-suggestion general-button rounded-pill bg-salmon hovered-button'
                        onClick={() => setValue(query)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                <div className='d-flex align-items-center justify-content-end pt-3'>
                  <button
                    className={`rounded-pill border general-button third-button px-4 py-2  ${value.trim() === '' || value.length > CHAR_LENGTH ? ' light-50-text bg-grey-80' : 'text-white bg-main-blue '}`}
                    disabled={value.trim() === '' || value.length > CHAR_LENGTH}
                    onClick={() => searchFunctionality({ manifestosSelected })}
                  >
                    {searchQuery !== '' &&
                    value === searchQuery &&
                    manifestosSelected?.map((manifesto) => manifesto.hash).join(',') === hashes.current
                      ? 'Your analysis'
                      : 'Search manifestos'}{' '}
                    <span
                      className='icon-long-arrow-right ml-1 d-inline-block-centered centered-1'
                      style={{ fontSize: '8px' }}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManifestosTracker;
